/**
 * Category grouping helpers for CustomHits.
 * Groups hits by each listing's deepest assigned store category level.
 * Ancestor context (for H2/H3/H4 headings) is derived from the full path stored in each level key.
 */

import { normalizeString } from "../../../../util/data";
import {
    mapCategoryIdToHierarchicalValue,
    getCategoryDepthFromId,
} from "../../../../util/dateRangeHelpers";
import { getVisibleCategoryIds } from "../../../../util/storeCategoryVisibility";

const LVL_KEYS = ['storeCategory.lvl0', 'storeCategory.lvl1', 'storeCategory.lvl2', 'storeCategory.lvl3', 'storeCategory.lvl4'];

/** publicData ID field for each level index (lvl1+ only; lvl0 is marketplace with no custom ID) */
const LEVEL_ID_KEYS = {
    1: 'storeCategoryId',
    2: 'storeSubCategoryId',
    3: 'storeGrandChildCategoryId',
    4: 'storeGreatGrandChildCategoryId',
};

const HIERARCHICAL_SEPARATOR = ' > ';

/** publicData key for per-category listing order at each level */
const ORDER_KEYS_BY_GROUP_LEVEL = {
    0: 'storeCategoryOrder',
    1: 'storeCategoryOrder',
    2: 'storeSubCategoryOrder',
    3: 'storeGrandChildCategoryOrder',
    4: 'storeGreatGrandChildCategoryOrder',
};
const FALLBACK_ORDER = 999;

/**
 * Get the publicData order key based on the listing's actual deepest assigned category depth.
 * Uses the deepest non-null AND visible category ID field rather than the Algolia lvl index.
 * When the marketplace group is hidden, computeHierarchy shifts the hierarchy by one level
 * (subcategory → lvl1, grandchild → lvl2, etc.), so ORDER_KEYS_BY_GROUP_LEVEL[deepestLevel]
 * would pick the wrong key. Reading from ID fields is always accurate.
 * Returns null when no visible ID is found so the caller's ORDER_KEYS_BY_GROUP_LEVEL fallback
 * handles legacy listings that pre-date the per-depth ID fields.
 */
function getActualDepthOrderKey(pd, visibleCategoryIds) {
    if (!pd) return null;
    if (pd.storeGreatGrandChildCategoryId && visibleCategoryIds?.has(pd.storeGreatGrandChildCategoryId))
        return 'storeGreatGrandChildCategoryOrder';
    if (pd.storeGrandChildCategoryId && visibleCategoryIds?.has(pd.storeGrandChildCategoryId))
        return 'storeGrandChildCategoryOrder';
    if (pd.storeSubCategoryId && visibleCategoryIds?.has(pd.storeSubCategoryId))
        return 'storeSubCategoryOrder';
    if (pd.storeCategoryId && visibleCategoryIds?.has(pd.storeCategoryId))
        return 'storeCategoryOrder';
    return null;
}

/**
 * Get the publicData order key used to sort hits within a section for the given groupByLevel.
 * @param {number} groupByLevel - 0, 1, 2, or 3
 * @returns {string}
 */
export function getOrderKeyForGroupByLevel(groupByLevel) {
    return ORDER_KEYS_BY_GROUP_LEVEL[groupByLevel] ?? 'storeCategoryOrder';
}

/**
 * Sort hits within a section by the store's chosen order for that level.
 * @param {Object[]} hits
 * @param {number} groupByLevel
 * @returns {Object[]}
 */
export function sortSectionHitsByOrder(hits, groupByLevel) {
    if (!hits?.length) return hits;
    const orderKey = getOrderKeyForGroupByLevel(groupByLevel);
    return [...hits].sort(
        (a, b) =>
            (a?.publicData?.[orderKey] ?? FALLBACK_ORDER) - (b?.publicData?.[orderKey] ?? FALLBACK_ORDER)
    );
}

/**
 * Find the deepest assigned store category level for a hit and parse it into
 * label + ancestor parts.
 *
 * lvl0 = marketplace label only (e.g. "Furniture")
 * lvl1 = marketplace + root store cat (e.g. "Furniture > Sofas")
 * lvl2 = marketplace + root + sub (e.g. "Furniture > Sofas > Leather Sofas")
 * etc.
 *
 * The deepest non-null level is the most specific category for this listing.
 * Its full path value already contains every ancestor separated by ' > '.
 *
 * @param {Object} hit - Algolia hit with publicData
 * @returns {{ sectionPath: string|null, sectionLabel: string|null, ancestors: string[], deepestLevel: number }}
 */
export function getDeepestSectionInfoForHit(hit) {
    const pd = hit?.publicData ?? {};
    for (let level = LVL_KEYS.length - 1; level >= 0; level--) {
        const val = pd[LVL_KEYS[level]];
        if (val && String(val).trim()) {
            const path = String(val).trim();
            const parts = path.split(HIERARCHICAL_SEPARATOR).filter(Boolean);
            return {
                sectionPath: path,
                sectionLabel: parts[parts.length - 1],
                ancestors: parts.slice(0, -1),
                deepestLevel: level,
            };
        }
    }
    return { sectionPath: null, sectionLabel: null, ancestors: [], deepestLevel: -1 };
}

/**
 * Like getDeepestSectionInfoForHit but skips levels whose custom store category
 * is not in visibleCategoryIds. Walks up to the nearest visible ancestor so
 * listings in hidden categories surface under a visible parent section.
 * Level 0 (marketplace label) is always used as the final fallback.
 *
 * @param {Object} hit
 * @param {Set<string>|null} visibleCategoryIds - null means no filtering
 * @returns {{ sectionPath: string|null, sectionLabel: string|null, ancestors: string[], deepestLevel: number }}
 */
function getVisibleSectionInfoForHit(hit, visibleCategoryIds) {
    if (!visibleCategoryIds) return getDeepestSectionInfoForHit(hit);

    const pd = hit?.publicData ?? {};
    for (let level = LVL_KEYS.length - 1; level >= 0; level--) {
        const val = pd[LVL_KEYS[level]];
        if (!val || !String(val).trim()) continue;

        const path = String(val).trim();
        const parts = path.split(HIERARCHICAL_SEPARATOR).filter(Boolean);

        // Level 0 is the marketplace label — always the final fallback, no ID to check
        if (level === 0) {
            return { sectionPath: path, sectionLabel: parts[parts.length - 1], ancestors: parts.slice(0, -1), deepestLevel: level };
        }

        // For levels 1+, verify the category is visible
        const idKey = LEVEL_ID_KEYS[level];
        const id = idKey ? pd[idKey] : null;
        // No ID stored (legacy listing) or category is visible → use this level
        if (!id || visibleCategoryIds.has(id)) {
            return { sectionPath: path, sectionLabel: parts[parts.length - 1], ancestors: parts.slice(0, -1), deepestLevel: level };
        }
        // Hidden category — continue loop to try shallower ancestor
    }

    return { sectionPath: null, sectionLabel: null, ancestors: [], deepestLevel: -1 };
}

/**
 * Get the group-by level index (0–3) from current category selection.
 * Kept for legacy/fallback usage.
 */
export function getGroupByLevel(selectedCategoryFilter) {
    if (!selectedCategoryFilter?.categoryId) return 0;
    const depth = selectedCategoryFilter.depth;
    if (depth >= 1 && depth <= 4) return depth;
    return 0;
}

/**
 * Get the section key for a hit at a fixed group-by level.
 * Used only in the no-store-categories fallback path.
 */
export function getSectionKeyForHit(hit, groupByLevel) {
    const pd = hit?.publicData ?? {};
    const keyAtLevel = pd[LVL_KEYS[groupByLevel]];
    if (keyAtLevel && String(keyAtLevel).trim()) return String(keyAtLevel).trim();
    return 'Uncategorized';
}

/**
 * Group hits into sections by a fixed level (used only in the fallback path
 * when the store has no store categories configured).
 */
export function groupHitsBySection(items, groupByLevel) {
    if (!items?.length) return [];

    const map = new Map();
    const order = [];

    for (const hit of items) {
        const label = getSectionKeyForHit(hit, groupByLevel);
        if (!map.has(label)) {
            map.set(label, []);
            order.push(label);
        }
        map.get(label).push(hit);
    }

    let defaultCatLabel = null;
    if (order.includes('Uncategorized')) {
        const index = order.indexOf('Uncategorized');
        const firstHit = map.get('Uncategorized')[0];
        const defaultCat = firstHit && firstHit.publicData && firstHit.publicData.category
            ? normalizeString(firstHit.publicData.category)
            : 'Uncategorized';
        defaultCatLabel = defaultCat;
        order.splice(index, 1);
        order.unshift(defaultCat);
        map.set(defaultCat, map.get('Uncategorized'));
        map.delete('Uncategorized');
    }

    return order.map(sectionLabel => ({
        sectionLabel,
        sectionPath: sectionLabel,
        ancestors: [],
        hits: map.get(sectionLabel),
        hideLabel: sectionLabel === defaultCatLabel,
    }));
}

/**
 * Build an ordered list of section labels from the store's category configuration.
 * Called with groupByLevel=4 to emit the full path for every category at every depth
 * in one DFS pass, giving the complete ordered label list for deepest-level grouping.
 *
 * @param {Object} user
 * @param {number} groupByLevel - pass 4 to get all paths at all depths
 * @param {string} locale
 * @returns {string[]}
 */
export function buildOrderedSectionLabels(user, groupByLevel, locale = "en") {
    const profile = user?.attributes?.profile || {};
    const publicData = profile.publicData || {};
    const storeCategories = Array.isArray(publicData.storeCategories) ? publicData.storeCategories : [];
    const storeCategoryGroups = publicData.storeCategoryGroups || {};

    if (!storeCategories.length) return [];

    const keyForParent = (p) => (p == null || p === undefined ? "null" : p);
    const byParent = new Map();
    storeCategories.forEach((c) => {
        const key = keyForParent(c.parentId);
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key).push(c);
    });

    byParent.forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));

    const allKeys = [...byParent.keys()];
    const mpKeys = allKeys.filter((k) => typeof k === "string" && k.startsWith("mp:"));

    const savedOrder = Array.isArray(storeCategoryGroups.__order) ? storeCategoryGroups.__order : [];
    const orderedTopKeys = [
        ...(byParent.has("null") ? ["null"] : []),
        ...savedOrder.filter((k) => mpKeys.includes(k)),
        ...mpKeys.filter((k) => !savedOrder.includes(k)),
    ];

    const orderedCategoryIds = [];
    function visit(parentKey) {
        const siblings = byParent.get(parentKey) || [];
        siblings.forEach((c) => {
            orderedCategoryIds.push(c.id);
            visit(`sc:${c.id}`);
        });
    }
    orderedTopKeys.forEach((k) => visit(k));

    const labels = [];
    const seen = new Set();

    orderedCategoryIds.forEach((categoryId) => {
        const depth = getCategoryDepthFromId(categoryId, storeCategories);
        if (!depth) return;

        const fullPath = mapCategoryIdToHierarchicalValue(categoryId, depth, user, locale);
        if (!fullPath) return;

        const parts = fullPath.split(" > ").filter(Boolean);
        if (!parts.length) return;

        const sliceCount = Math.min(groupByLevel + 1, parts.length);
        const label = parts.slice(0, sliceCount).join(" > ");

        if (!label || seen.has(label)) return;
        seen.add(label);
        labels.push(label);
    });

    return labels;
}

/**
 * Reorder grouped sections to follow a list of ordered labels.
 * Sections not in orderedLabels are appended at the end in their original order.
 *
 * @param {{ sectionLabel: string, hits: Object[] }[]} sections
 * @param {string[]} orderedLabels
 * @param {function} keyFn - extracts the match key from a section (default: sectionLabel)
 */
export function reorderSectionsByOwnerOrder(sections, orderedLabels, keyFn = s => s.sectionLabel) {
    if (!sections?.length || !orderedLabels?.length) return sections || [];

    const byKey = new Map();
    sections.forEach((sec) => {
        const key = keyFn(sec);
        if (!byKey.has(key)) {
            byKey.set(key, sec);
        }
    });

    const orderedSet = new Set();
    const result = [];

    orderedLabels.forEach((label) => {
        const sec = byKey.get(label);
        if (sec) {
            result.push(sec);
            orderedSet.add(keyFn(sec));
        }
    });

    sections.forEach((sec) => {
        if (!orderedSet.has(keyFn(sec))) {
            result.push(sec);
        }
    });

    return result;
}

/**
 * Group hits by their deepest assigned store category (lvl0–lvl4).
 * Listings without any store category fall back to marketplace category label.
 *
 * Each section carries ancestor path parts so the render layer can show
 * hierarchical H2/H3/H4 context labels above the H1 section heading.
 *
 * @param {Object[]} items - Sorted hits
 * @param {Object} user - Store owner user object (with storeCategories resolved)
 * @param {string} locale
 * @returns {{ sectionLabel: string, sectionPath: string, ancestors: string[], hits: Object[], hideLabel: boolean }[]}
 */
function buildDeepestLevelGroupedSections(items, user, locale, visibleCategoryIds) {
    if (!items?.length) return [];

    const sectionMap = new Map();
    const sectionOrder = [];

    for (const hit of items) {
        const info = getVisibleSectionInfoForHit(hit, visibleCategoryIds);

        let key;
        let sectionMeta;

        if (info.sectionPath) {
            key = info.sectionPath;
            if (!sectionMap.has(key)) {
                sectionMeta = {
                    sectionLabel: info.sectionLabel,
                    sectionPath: info.sectionPath,
                    ancestors: info.ancestors,
                    deepestLevel: info.deepestLevel,
                    orderKey: getActualDepthOrderKey(hit?.publicData, visibleCategoryIds),
                    hits: [],
                    hideLabel: false,
                };
                sectionMap.set(key, sectionMeta);
                sectionOrder.push(key);
            }
        } else {
            // No store category at all: fall back to marketplace category
            const mpCat = hit?.publicData?.category
                ? normalizeString(hit.publicData.category)
                : null;
            key = mpCat || 'Uncategorized';
            if (!sectionMap.has(key)) {
                sectionMeta = {
                    sectionLabel: key,
                    sectionPath: key,
                    ancestors: [],
                    deepestLevel: -1,
                    hits: [],
                    hideLabel: true, // uncategorized listings: hide section label
                };
                sectionMap.set(key, sectionMeta);
                sectionOrder.push(key);
            }
        }

        sectionMap.get(key).hits.push(hit);
    }

    // Sort hits within each section by the appropriate order key.
    // Use section.orderKey (derived from actual category depth via ID fields) so the correct
    // field is chosen even when the marketplace group is hidden and hierarchy levels are shifted.
    sectionMap.forEach((section) => {
        if (section.deepestLevel >= 0) {
            const orderKey = section.orderKey
                || ORDER_KEYS_BY_GROUP_LEVEL[section.deepestLevel]
                || 'storeCategoryOrder';
            section.hits = [...section.hits].sort(
                (a, b) =>
                    (a?.publicData?.[orderKey] ?? FALLBACK_ORDER) -
                    (b?.publicData?.[orderKey] ?? FALLBACK_ORDER)
            );
        }
    });

    const rawSections = sectionOrder.map((key) => sectionMap.get(key));

    // Order sections by owner's configured category order.
    // buildOrderedSectionLabels(user, 4, locale) emits the full path for every
    // category at every depth in DFS / owner order — exactly the keys we need.
    const orderedPaths = buildOrderedSectionLabels(user, 4, locale);
    if (!orderedPaths.length) return rawSections;

    return reorderSectionsByOwnerOrder(rawSections, orderedPaths, s => s.sectionPath);
}

/**
 * Build category sections in final storefront display order.
 *
 * When the store has configured store categories, listings are always grouped
 * by their deepest assigned level (requirement: "no matter if the category is
 * selected or not"). The selectedCategoryFilter only affects which Algolia hits
 * are returned — it no longer changes the grouping level.
 *
 * When no store categories exist, falls back to marketplace-category grouping.
 *
 * @param {Object[]} items
 * @param {Object} options
 * @param {Object} options.user
 * @param {Object} options.selectedCategoryFilter - kept for API compatibility; no longer used for grouping level
 * @param {string} options.locale
 * @returns {{ sectionLabel: string, sectionPath: string, ancestors: string[], hits: Object[], hideLabel: boolean }[]}
 */
export function buildGroupedSections(items, { user, selectedCategoryFilter, locale = 'en' }) {
    if (!items?.length) return [];

    const profile = user?.attributes?.profile || {};
    const publicData = profile.publicData || {};
    const storeCategories = Array.isArray(publicData.storeCategories)
        ? publicData.storeCategories
        : [];
    const hasStoreCategories = storeCategories.length > 0;

    if (hasStoreCategories) {
        const storeCategoryGroups = publicData.storeCategoryGroups || {};
        const { visibleCategoryIds } = getVisibleCategoryIds(storeCategories, storeCategoryGroups);
        return buildDeepestLevelGroupedSections(items, user, locale, visibleCategoryIds);
    }

    // Fallback: store has no store categories — group by marketplace category (lvl0)
    const grouped = groupHitsBySection(items, 0);
    return grouped;
}

/**
 * Flatten sections into the exact final display order.
 */
export function flattenSectionsInDisplayOrder(sections) {
    if (!sections?.length) return [];
    return sections.flatMap(section => section.hits || []);
}

/**
 * Rebuild section containers from a visible hit subset while preserving
 * the original section ordering and hiding empty sections.
 */
export function rebuildSectionsFromVisibleHits(sections, visibleHits) {
    if (!sections?.length || !visibleHits?.length) return [];

    const visibleById = new Map(visibleHits.map(hit => [hit.objectID, hit]));
    const result = [];

    sections.forEach(section => {
        const hits = (section.hits || []).filter(hit => visibleById.has(hit.objectID));
        if (hits.length) {
            result.push({
                sectionLabel: section.sectionLabel,
                sectionPath: section.sectionPath,
                ancestors: section.ancestors || [],
                hits,
                hideLabel: section.hideLabel,
            });
        }
    });

    return result;
}
